import React, { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useUsersStore } from '../../store/useUsersStore';
import { getAllCountries, searchCountries } from '../../utils/countriesHelper';


const CountriesFilter = ({ subUserId: propSubUserId }) => {
  // ============ STORE SUBSCRIPTIONS ============
  const users = useUsersStore((s) => s.users);
  const selectedKey = useUsersStore((s) => s.selectedKey);
  const updateSubUser = useUsersStore((s) => s.updateSubUser);
  const storeError = useUsersStore((s) => s.error);


  // ============ RESOLVE SUB-USER ID ============
  const resolvedSubUserId = useMemo(() => {
    if (propSubUserId) return String(propSubUserId);
    if (!selectedKey || !Array.isArray(users)) return null;
    
    const found = users.find((u) => {
      const id = u.sub_user_id || u.document_id || u.id;
      if (String(id) === String(selectedKey)) return true;
      if (u.sub_username && String(u.sub_username) === String(selectedKey)) return true;
      return false;
    });
    return found ? String(found.sub_user_id || found.document_id || found.id) : null;
  }, [propSubUserId, selectedKey, users]);


  const finalSubUserId = useMemo(
    () => String(resolvedSubUserId || propSubUserId || selectedKey || '') || null,
    [resolvedSubUserId, propSubUserId, selectedKey]
  );


  // ============ GET PROJECT FILTERS ============
  const projectFilters = useMemo(() => {
    if (!finalSubUserId || !Array.isArray(users)) return {};
    const u = users.find((x) => String(x.sub_user_id || x.document_id || x.id) === String(finalSubUserId));
    return (u && u.project_filters) || {};
  }, [finalSubUserId, users]);


  // ============ GET ALL AVAILABLE COUNTRIES ============
  const allCountries = useMemo(() => getAllCountries(), []);


  // ============ INITIAL EXCLUDED STATE ============
  const initialExcluded = useMemo(() => {
    const val = projectFilters.excluded_countries;
    if (Array.isArray(val)) return val.slice();
    if (typeof val === 'string' && val.trim()) return val.split(',').map(s => s.trim());
    return [];
  }, [projectFilters.excluded_countries]);


  const initialExcludedJson = useMemo(() => JSON.stringify(initialExcluded || []), [initialExcluded]);


  // ============ LOCAL STATE ============
  const [excluded, setExcluded] = useState(initialExcluded);
  const [searchInput, setSearchInput] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const prevInitialRef = useRef(initialExcludedJson);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);


  // ============ DETERMINE ACTIVE STATE (Inverted for exclusion) ============
  const selectionState = useMemo(() => {
    if (excluded.length === 0) return 'none'; // No exclusions = All allowed
    if (excluded.length === allCountries.length) return 'all'; // All excluded = None allowed
    return 'partial';
  }, [excluded.length, allCountries.length]);


  // ============ SYNC FROM STORE ============
  useEffect(() => {
    if (prevInitialRef.current !== initialExcludedJson) {
      setExcluded(initialExcluded);
      prevInitialRef.current = initialExcludedJson;
    }
    setMsg(null);
    setErr(null);
  }, [initialExcludedJson, resolvedSubUserId, initialExcluded]);


  // ============ FILTER COUNTRIES BASED ON SEARCH ============
  const filteredCountries = useMemo(() => {
    if (!searchInput.trim()) return [];
    return searchCountries(searchInput).filter(c => !excluded.includes(c));
  }, [searchInput, excluded]);


  // ============ HANDLERS ============
  const addCountry = useCallback((country) => {
    setExcluded(prev => {
      const next = Array.isArray(prev) ? [...prev] : [];
      if (!next.includes(country)) {
        next.push(country);
      }
      return next;
    });
    setSearchInput('');
    setIsDropdownOpen(false);
    inputRef.current?.focus();
  }, []);


  const removeCountry = useCallback((country) => {
    setExcluded(prev => prev.filter(c => c !== country));
  }, []);


  const excludeAll = useCallback(() => {
    setExcluded(allCountries.slice());
  }, [allCountries]);


  const clearAll = useCallback(() => {
    setExcluded([]);
    setSearchInput('');
    setIsDropdownOpen(false);
  }, []);


  const handleSubmit = useCallback(async () => {
    setErr(null);
    setMsg(null);
    if (!finalSubUserId) {
      setErr('No sub-user selected');
      return;
    }


    const payload = {
      project_filters: {
        ...(projectFilters || {}),
        excluded_countries: Array.isArray(excluded) ? excluded.slice() : [],
      },
    };


    setSaving(true);
    try {
      await updateSubUser(finalSubUserId, payload);
      setMsg('✅ Saved successfully');
    } catch (e) {
      console.error('Countries save failed', e);
      setErr(`❌ ${e?.message || 'Save failed'}`);
    } finally {
      setSaving(false);
    }
  }, [finalSubUserId, excluded, projectFilters, updateSubUser]);


  const handleReset = useCallback(() => {
    setExcluded(initialExcluded);
    setSearchInput('');
    setIsDropdownOpen(false);
    setMsg(null);
    setErr(null);
  }, [initialExcluded]);


  // ============ CLOSE DROPDOWN ON OUTSIDE CLICK ============
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && 
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  // ============ RENDER ============
  return (
    <div className="w-full p-6 bg-white rounded-lg shadow">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Exclude Countries</h2>
        <p className="text-sm text-gray-600 mt-2">
          BidMasterPro will skip projects from these client countries.
        </p>
      </div>


      {/* Action Buttons with Active State Indicator */}
      <div className="flex flex-wrap gap-2 items-center mb-6">
        <div className="flex gap-2 mb-6 bg-white p-2 rounded-xl shadow-sm w-full border-2 border-light-blue">
          {/* Exclude All Button */}
          <button
            type="button"
            onClick={excludeAll}
            disabled={saving}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 flex-1 ${
              selectionState === 'all'
                ? 'bg-primary-blue text-white shadow-md'
                : 'bg-light-blue-shade-20 text-primary-blue hover:bg-gray-50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Exclude All
          </button>


          {/* Active State Indicator (Middle Button) */}
          <div className="flex-1 px-6 py-2 rounded-lg font-medium transition-all duration-200 bg-primary-blue text-white shadow-md flex items-center justify-center">
            {selectionState === 'all' && (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd"/>
                </svg>
                All Excluded
              </span>
            )}
            {selectionState === 'none' && (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                All Allowed
              </span>
            )}
            {selectionState === 'partial' && (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd"/>
                </svg>
                Partial ({excluded.length}/{allCountries.length})
              </span>
            )}
          </div>


          {/* Clear All Button */}
          <button
            type="button"
            onClick={clearAll}
            disabled={saving}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 flex-1 ${
              selectionState === 'none'
                ? 'bg-primary-blue text-white shadow-md'
                : 'bg-light-blue-shade-20 text-primary-blue hover:bg-gray-50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Clear All
          </button>
        </div>


        {/* Status Messages */}
        <div className="ml-auto flex items-center gap-2">
          {saving && <span className="text-blue-600 text-sm font-medium">⏳ Saving...</span>}
          {msg && <span className="text-green-600 text-sm font-medium">{msg}</span>}
          {err && <span className="text-red-600 text-sm font-medium">{err}</span>}
          {storeError && !err && <span className="text-red-600 text-sm font-medium">{storeError}</span>}
        </div>
      </div>


      {/* Search Input - Primary Blue Border */}
      <div className="mb-6 relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Search & Add Countries to Exclude
        </label>


        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search countries (e.g., Pakistan, India, USA)..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => searchInput && setIsDropdownOpen(true)}
            className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-2 focus:border-primary-blue"
          />
             <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60">
                <img src="/Icons/search.svg" alt="Search" className="w-5 h-5" />
              </div>

          {/* Dropdown Results */}
          {isDropdownOpen && searchInput && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-0 right-0 mt-1 bg-white border-[1.5px] border-primary-blue rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto"
            >
              {filteredCountries.length > 0 ? (
                filteredCountries.map(country => (
                  <button
                    key={country}
                    onClick={() => addCountry(country)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 text-sm font-medium text-gray-700 transition"
                  >
                    <div className="font-semibold text-primary-blue">{country}</div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-center text-gray-500 text-sm">
                  No countries match "{searchInput}"
                </div>
              )}
            </div>
          )}
        </div>


        <p className="text-xs text-gray-500 mt-2">
          {excluded.length > 0
            ? `${excluded.length} countries excluded`
            : 'Start typing to search and exclude countries'}
        </p>
      </div>


      {/* Excluded Countries - Styled like Currency Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Excluded Countries ({excluded.length})
        </label>


        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {excluded.length === 0 ? (
            <div className="col-span-full text-sm text-gray-500">No countries excluded.</div>
          ) : (
            excluded.map((country) => (
              <div 
                key={country} 
                className="flex items-center justify-between gap-3 rounded px-4 py-2 bg-light-blue border border-primary-blue"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-gray-800">{country}</span>
                </div>
                <button
                  title="Remove from exclusion list"
                  onClick={() => removeCountry(country)}
                  className="rounded flex items-center justify-center hover:bg-red-50 transition-colors"
                  disabled={saving}
                  style={{ width: 28, height: 28 }}
                >
                  <img src="/Icons/cross-icon.svg" alt="Remove" className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>


      {/* Footer Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleReset}
          disabled={saving}
          className="px-5 py-2 border-[1.5px] border-primary-blue text-primary-blue rounded-lg hover:bg-primary-blue hover:text-white disabled:opacity-60 transition font-medium"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || !finalSubUserId}
          className="px-6 py-2.5 bg-primary-blue text-white rounded-lg hover:bg-primary-blue/60 disabled:bg-gray-400 disabled:cursor-not-allowed transition font-medium"
        >
          {saving ? ' Saving...' : ' Save Changes'}
        </button>
      </div>
    </div>
  );
};


CountriesFilter.propTypes = {
  subUserId: PropTypes.string,
};


export default memo(CountriesFilter);
